// WOS-BUNDLE: Setup › Tags (managers). Work OS has no tag admin; CompanyOS adds it here. Every
// company tag with how many tasks carry it; on hover a row shows its actions: rename inline (Enter
// saves, Escape cancels), recolour from the swatches, merge into another tag (its tasks get that
// tag; confirmed) and delete (taken off every task; confirmed). "New tag" makes one.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TagChip,
  TagSwatch,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MergeIcon, PaletteIcon, PencilIcon, PlusIcon, TagIcon, Trash2Icon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { cleanTagName, TAG_SWATCHES, type TagColor, type TagView } from "../work/tag-logic";
import { tagKeys, tagsQuery } from "../work/tags";
import { invalidateWork } from "../work/work-queries";
import { DENSE_TABLE, RowAction, RowActions } from "./row-actions";
import { SetupPage } from "./setup-page";

export function TagsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useQuery(tagsQuery(api, co));
  const rows = q.data ?? [];
  const [renaming, setRenaming] = useState<string | null>(null);
  const [merging, setMerging] = useState<TagView | null>(null);
  const [deleting, setDeleting] = useState<TagView | null>(null);
  const [creating, setCreating] = useState(false);
  const refresh = () => invalidateWork(qc, co);

  const update = useMutation({
    mutationFn: (v: { id: string; name?: string; color?: TagColor }) =>
      api.client.tags.update({
        params: { co, id: v.id },
        body: { ...(v.name ? { name: v.name } : {}), ...(v.color ? { color: v.color } : {}) },
      }),
    onMutate: (v) => {
      qc.setQueryData<TagView[]>(tagKeys.all(co), (d) =>
        d?.map((t) =>
          t.id === v.id
            ? { ...t, ...(v.name ? { name: v.name } : {}), ...(v.color ? { color: v.color } : {}) }
            : t,
        ),
      );
    },
    onSuccess: () => setRenaming(null),
    onSettled: refresh,
  });
  const create = useMutation({
    mutationFn: (name: string) => api.client.tags.create({ params: { co }, body: { name } }),
    onSuccess: (t) => {
      setCreating(false);
      toast.add({ title: `Tag “${t.name}” made`, type: "success" });
    },
    onSettled: refresh,
  });
  const merge = useMutation({
    mutationFn: (v: { from: TagView; into: string }) =>
      api.client.tags.merge({ params: { co, id: v.from.id }, body: { into_tag_id: v.into } }),
    onSuccess: (into, v) => {
      setMerging(null);
      toast.add({ title: `“${v.from.name}” merged into “${into.name}”`, type: "success" });
    },
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (t: TagView) => api.client.tags.delete({ params: { co, id: t.id } }),
    onSuccess: (_d, t) => {
      setDeleting(null);
      toast.add({ title: `Tag “${t.name}” deleted` });
    },
    onSettled: refresh,
  });

  const newButton = (
    <Button size="sm" onClick={() => setCreating(true)}>
      <PlusIcon data-icon="inline-start" />
      New tag
    </Button>
  );
  return (
    <SetupPage title="Tags" {...(q.data ? { count: rows.length } : {})} actions={newButton}>
      {creating ? (
        <NameForm
          label="New tag name"
          initial=""
          pending={create.isPending}
          onSave={(name) => create.mutate(name)}
          onCancel={() => setCreating(false)}
          className="mb-3 max-w-sm"
        />
      ) : null}
      {q.isPending ? (
        <Loading label="Loading tags" rows={4} />
      ) : q.isError ? (
        <LoadError what="Tags" error={q.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={TagIcon}
          title="No tags yet"
          description="Tags group tasks across boards. Make one here, or type a new name in a task's Tags."
          actions={newButton}
        />
      ) : (
        <Table data-slot="setup-tags" className={DENSE_TABLE}>
          <TableCaption className="sr-only">The company's tags.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Tag</TableHead>
              <TableHead scope="col" className="text-right">
                Tasks
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id} className="group/row" data-tag-row={t.name}>
                <TableCell>
                  {renaming === t.id ? (
                    <NameForm
                      label={`Rename ${t.name}`}
                      initial={t.name}
                      pending={update.isPending}
                      onSave={(name) =>
                        name === t.name ? setRenaming(null) : update.mutate({ id: t.id, name })
                      }
                      onCancel={() => setRenaming(null)}
                    />
                  ) : (
                    <TagChip name={t.name} color={t.color} />
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {t.task_count}
                </TableCell>
                <TableCell className="w-40">
                  <RowActions>
                    <RowAction
                      icon={PencilIcon}
                      label={`Rename ${t.name}`}
                      tip="Rename"
                      onClick={() => setRenaming(t.id)}
                    />
                    <ColorPicker tag={t} onPick={(color) => update.mutate({ id: t.id, color })} />
                    <RowAction
                      icon={MergeIcon}
                      label={`Merge ${t.name} into another tag`}
                      tip="Merge into…"
                      disabled={rows.length < 2}
                      onClick={() => setMerging(t)}
                    />
                    <RowAction
                      icon={Trash2Icon}
                      label={`Delete ${t.name}`}
                      tip="Delete"
                      className="hover:text-danger-text"
                      onClick={() => setDeleting(t)}
                    />
                  </RowActions>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {merging ? (
        <MergeDialog
          tag={merging}
          others={rows.filter((x) => x.id !== merging.id)}
          pending={merge.isPending}
          onMerge={(into) => merge.mutate({ from: merging, into })}
          onClose={() => setMerging(null)}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(o) => (o ? null : setDeleting(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              It comes off {deleting?.task_count ?? 0} task
              {deleting?.task_count === 1 ? "" : "s"}, for everyone. Merge it into another tag
              instead to keep those tasks tagged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deleting && remove.mutate(deleting)}
            >
              Delete tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SetupPage>
  );
}

function NameForm({
  label,
  initial,
  pending,
  onSave,
  onCancel,
  className,
}: {
  label: string;
  initial: string;
  pending: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
  className?: string;
}) {
  const [name, setName] = useState(initial);
  const clean = cleanTagName(name);
  return (
    <form
      className={`flex items-center gap-2 ${className ?? ""}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (clean) onSave(clean);
      }}
    >
      <Input
        aria-label={label}
        autoFocus
        className="h-8"
        value={name}
        maxLength={60}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
      />
      <Button size="sm" type="submit" disabled={!clean || pending}>
        Save
      </Button>
      <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}

function ColorPicker({ tag, onPick }: { tag: TagView; onPick: (c: TagColor) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={<RowAction icon={PaletteIcon} label={`Recolour ${tag.name}`} tip="Colour" />}
      />
      <PopoverContent align="end" className="w-auto p-2" aria-label={`Colours for ${tag.name}`}>
        <fieldset className="m-0 grid grid-cols-5 gap-1.5 border-0 p-0">
          <legend className="sr-only">Tag colour</legend>
          {TAG_SWATCHES.map((s) => (
            <button
              key={s.color}
              type="button"
              aria-pressed={tag.color === s.color}
              aria-label={s.label}
              title={s.label}
              className="rounded-full p-0.5 hover:bg-muted"
              onClick={() => {
                onPick(s.color);
                setOpen(false);
              }}
            >
              <TagSwatch color={s.color} selected={tag.color === s.color} />
            </button>
          ))}
        </fieldset>
      </PopoverContent>
    </Popover>
  );
}

function MergeDialog({
  tag,
  others,
  pending,
  onMerge,
  onClose,
}: {
  tag: TagView;
  others: TagView[];
  pending: boolean;
  onMerge: (into: string) => void;
  onClose: () => void;
}) {
  const [into, setInto] = useState(others[0]?.id ?? "");
  const id = useId();
  const target = others.find((t) => t.id === into);
  return (
    <AlertDialog open onOpenChange={(o) => (o ? null : onClose())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Merge “{tag.name}” into another tag</AlertDialogTitle>
          <AlertDialogDescription>
            Its {tag.task_count} task{tag.task_count === 1 ? "" : "s"} get the tag you pick, and “
            {tag.name}” is deleted. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <label htmlFor={id} className="text-sm font-medium">
          Merge into
        </label>
        <NativeSelect id={id} value={into} onChange={(e) => setInto(e.target.value)}>
          {others.map((t) => (
            <NativeSelectOption key={t.id} value={t.id}>
              {t.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={!target || pending} onClick={() => onMerge(into)}>
            Merge into {target?.name ?? "…"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
